<!-- =========================================================================================
  File Name: EquipmentOperatorBulkEditSidebar.vue
  Description: Bulk edit the full set of Equipment linked to a single Operator.
  Selecting an Employee pre-loads their current Equipment links; Save replaces the full set
  (adds newly checked Equipment, removes unchecked Equipment).
========================================================================================== -->

<template>
  <vs-sidebar click-not-close position-right parent="body" default-index="1" color="primary"
    class="add-new-data-sidebar items-no-padding" spacer v-model="isSidebarActiveLocal">
    <div class="mt-6 flex items-center justify-between px-6">
      <h4>BULK EDIT OPERATOR'S EQUIPMENT</h4>
      <feather-icon icon="XIcon" @click.stop="isSidebarActiveLocal = false" class="cursor-pointer"></feather-icon>
    </div>
    <vs-divider class="mb-0"></vs-divider>

    <component :is="scrollbarTag" class="scroll-area--data-list-add-new" :settings="settings" :key="$vs.rtl">
      <div>
        <div class="vx-row pt-3 m-2">
          <div class="vx-col w-full">
            <span>Employee Type</span>
            <v-select placeholder="Filter Employee list by type" :options="employeeTypeOptions" :clearable="false"
              v-model="employeeTypeFilter"></v-select>
          </div>
        </div>
        <div class="vx-row pt-3 m-2">
          <div class="vx-col w-full">
            <span>Employee</span>
            <v-select placeholder="Select Employee" :options="filteredOperatorData" v-model="operatorSelect"></v-select>
          </div>
        </div>
        <div class="vx-row pt-3 m-2">
          <div class="vx-col w-full">
            <span>Equipment this Employee is authorised to use (Allowed)</span>
            <v-select placeholder="Select Equipment" :options="equipmentData" v-model="selectedEquipment"
              :disabled="!operatorSelect || !operatorSelect.value" multiple></v-select>
          </div>
        </div>
        <div class="vx-row pt-3 m-2">
          <div class="vx-col w-full">
            <span>Equipment this Employee is explicitly Blocked from using</span>
            <v-select placeholder="Select Equipment" :options="equipmentData" v-model="selectedBlockedEquipment"
              :disabled="!operatorSelect || !operatorSelect.value" multiple></v-select>
          </div>
        </div>
        <div class="vx-row pt-3 m-2" v-if="validationMessage">
          <div class="vx-col w-full">
            <vs-alert color="warning" :active="true">{{ validationMessage }}</vs-alert>
          </div>
        </div>
      </div>
    </component>
    <div class="flex flex-wrap items-center p-6" slot="footer">
      <vs-button class="rounded-lg m-1" color="success" type="gradient" @click="submitData" ref="submitButton"
        :disabled="!isFormValid" icon-pack="feather" icon="icon-check">Save</vs-button>
      <vs-button class="rounded-lg m-1" color="danger" type="gradient" @click="isSidebarActiveLocal = false"
        icon-pack="feather" icon="icon-x-square">Cancel</vs-button>
    </div>
  </vs-sidebar>
</template>

<script>
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import vSelect from "vue-select";

export default {
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true
    }
  },
  components: {
    VuePerfectScrollbar,
    "v-select": vSelect
  },
  data() {
    return {
      operatorData: [],
      equipmentData: [],
      operatorSelect: null,
      selectedEquipment: [],
      selectedBlockedEquipment: [],
      employeeTypeOptions: [
        { label: "All Types", value: 0 },
        { label: "Driver", value: 1 },
        { label: "Fuel Attendant", value: 2 },
        { label: "Authorizer", value: 3 }
      ],
      employeeTypeFilter: { label: "All Types", value: 0 },
      settings: {
        maxScrollbarLength: 60,
        wheelSpeed: 0.6
      }
    };
  },
  watch: {
    isSidebarActive(val) {
      if (!val) return;
      this.initValues();
      this.GetOperatorDropdown();
      this.GetEquipmentDropdown();
    },
    operatorSelect(val) {
      this.selectedEquipment = [];
      this.selectedBlockedEquipment = [];
      if (val && val.value) {
        this.GetEquipmentPerOperator(val.value);
      }
    },
    selectedEquipment(newVal) {
      if (!this.selectedBlockedEquipment || this.selectedBlockedEquipment.length === 0) return;
      const allowedValues = new Set(newVal.map((i) => i.value));
      const filtered = this.selectedBlockedEquipment.filter((i) => !allowedValues.has(i.value));
      if (filtered.length !== this.selectedBlockedEquipment.length) {
        this.selectedBlockedEquipment = filtered;
      }
    },
    selectedBlockedEquipment(newVal) {
      if (!this.selectedEquipment || this.selectedEquipment.length === 0) return;
      const blockedValues = new Set(newVal.map((i) => i.value));
      const filtered = this.selectedEquipment.filter((i) => !blockedValues.has(i.value));
      if (filtered.length !== this.selectedEquipment.length) {
        this.selectedEquipment = filtered;
      }
    }
  },
  computed: {
    isSidebarActiveLocal: {
      get() {
        return this.isSidebarActive;
      },
      set(val) {
        if (!val) {
          this.$emit("closeSidebar");
        }
      }
    },
    filteredOperatorData() {
      if (!this.employeeTypeFilter || !this.employeeTypeFilter.value) return this.operatorData;
      return this.operatorData.filter((item) => item.operatorTypeId === this.employeeTypeFilter.value);
    },
    validationMessage() {
      if (!this.operatorSelect || !this.operatorSelect.value) return "Select an Employee to continue.";
      return "";
    },
    isFormValid() {
      return this.validationMessage === "";
    },
    scrollbarTag() {
      return this.$store.getters.scrollbarTag;
    }
  },
  methods: {
    GetOperatorDropdown() {
      const self = this;
      var onSuccess = function (response) {
        self.operatorData = (response.data || []).map((item) => { return { label: item.name, value: item.id, operatorTypeId: item.operatorTypeId } });
      };
      var onFinally = function () { };
      self.$ajaxGet(self, "Operators/GetOperatordropdown", onSuccess, onFinally);
    },
    GetEquipmentDropdown() {
      const self = this;
      var onSuccess = function (response) {
        self.equipmentData = (response.data || []).map((item) => { return { label: item.registration, value: item.id } });
      };
      var onFinally = function () { };
      self.$ajaxGet(self, "Equipments/GetEquipmentDropDown", onSuccess, onFinally);
    },
    GetEquipmentPerOperator(operatorId) {
      const self = this;
      self.showLoadingOnElement("submitButton", 0.6, "point");
      var onSuccess = function (response) {
        var links = response.data || [];
        var allowedIds = links.filter((item) => !item.isBlocked).map((item) => item.equipmentId);
        var blockedIds = links.filter((item) => item.isBlocked).map((item) => item.equipmentId);
        self.selectedEquipment = self.equipmentData.filter((item) => allowedIds.includes(item.value));
        self.selectedBlockedEquipment = self.equipmentData.filter((item) => blockedIds.includes(item.value));
      };
      var onFinally = function () {
        self.$vs.loading.close(self.$refs.submitButton.$el);
      };
      self.$ajaxGet(self, `PerEquipmentOperators/GetEquipmentPerOperator?operatorId=${operatorId}`, onSuccess, onFinally);
    },
    initValues() {
      this.operatorSelect = null;
      this.selectedEquipment = [];
      this.selectedBlockedEquipment = [];
      this.employeeTypeFilter = { label: "All Types", value: 0 };
    },
    showLoadingOnElement(element, scale, type) {
      var self = this;
      self.$vs.loading({
        type: type,
        container: self.$refs[element].$el,
        scale: scale
      });
    },
    submitData() {
      var self = this;
      if (!self.isFormValid) {
        self.FailedCustomNotify(self.validationMessage);
        return;
      }

      const obj = {
        OperatorId: self.operatorSelect.value,
        EquipmentIds: self.selectedEquipment.map((i) => i.value),
        BlockedEquipmentIds: self.selectedBlockedEquipment.map((i) => i.value)
      };

      var onSuccess = function () {
        self.$emit("saved");
        self.isSidebarActiveLocal = false;
        self.initValues();
      };
      var onFinally = function () {
        self.$vs.loading.close(self.$refs.submitButton.$el);
      };
      self.$ajaxPut(self, "PerEquipmentOperators/BulkEditOperatorLinks", obj, onSuccess, onFinally);
    },
    FailedCustomNotify(msg) {
      this.$vs.notify({
        title: 'Not Successfull',
        text: msg,
        color: 'danger'
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.add-new-data-sidebar {
  ::v-deep .vs-sidebar--background {
    z-index: 52010;
  }

  ::v-deep .vs-sidebar {
    z-index: 52010;
    width: 700px;
    max-width: 90vw;
  }
}

.scroll-area--data-list-add-new {
  height: calc(var(--vh, 1vh) * 100 - 16px - 45px - 82px);

  &:not(.ps) {
    overflow-y: auto;
  }
}
</style>
